import { Box, Button, Flex, Icon, IconButton, Stack, Text, useToast } from '@chakra-ui/react';
import { GoBookmark, GoBookmarkFill } from 'react-icons/go';

import { useBookmarkActions } from './useBookmarkActions';

interface BookmarkToastProps {
  contentTitle: string | undefined | null;
  onUndo: () => void;
  action: 'add' | 'remove';
}

function BookmarkToast({ contentTitle, onUndo, action }: BookmarkToastProps) {
  const label = action === 'add' ? 'Bookmark Added' : 'Bookmark Removed';

  return (
    <Box
      paddingLeft={6}
      paddingRight={4}
      paddingY={3}
      backgroundColor="white"
      boxShadow="0px 4px 20px 0px rgba(0, 0, 0, .1)"
      borderRadius="full"
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Stack spacing={0}>
          <Text fontSize="14px" fontWeight={600} color="baseBlack">
            {label}
          </Text>
          {contentTitle && (
            <Text fontSize="12px" fontWeight={500} color="neutral.800">
              {contentTitle.length > 25 ? contentTitle.slice(0, 25) + '...' : contentTitle}
            </Text>
          )}
        </Stack>

        <Button variant="cancel" size="sm" borderRadius="full" onClick={onUndo}>
          Undo
        </Button>
      </Flex>
    </Box>
  );
}

interface BookmarkButtonProps {
  contentId: string | undefined | null;
  contentType: string | undefined | null;
  contentTitle: string | undefined | null;
  boxSize?: number;
  lightIcons?: boolean;
}

export default function BookmarkButton({
  contentId,
  contentType,
  contentTitle,
  boxSize = 7,
  lightIcons = false,
}: BookmarkButtonProps) {
  const toast = useToast();

  const { addBookmark, removeBookmark, listUserBookmarks } = useBookmarkActions();

  const { data, isLoading, isError } = listUserBookmarks();
  const isBookmarked = data?.edges.some((bookmark) => bookmark.node.content.id === contentId);
  const bookmarkId = data?.edges.find((bookmark) => bookmark.node.content.id === contentId)?.node
    .id;

  const showToast = (action: 'add' | 'remove') => {
    const isAdd = action === 'add';

    toast({
      position: 'top',
      render: () => (
        <BookmarkToast
          contentTitle={contentTitle}
          onUndo={async () => {
            toast.closeAll();
            if (isAdd) {
              await removeBookmark.mutateAsync(bookmarkId!);

              showToast('remove');
            } else {
              await addBookmark.mutateAsync({ contentId: contentId!, contentType: contentType! });

              showToast('add');
            }
          }}
          action={action}
        />
      ),
    });
  };

  if (isLoading || isError) return null;

  return (
    <IconButton
      variant="ghost"
      aria-label="Bookmark"
      icon={
        <Icon
          boxSize={boxSize}
          as={isBookmarked ? GoBookmarkFill : GoBookmark}
          color={isBookmarked ? 'brand.button' : lightIcons ? 'white' : 'brand.iconGrey'}
          _hover={{ color: 'brand.button' }}
        />
      }
      onClick={
        isBookmarked
          ? (e) => {
              e.stopPropagation();

              removeBookmark.mutateAsync(bookmarkId!).then(() => {
                showToast('remove');
              });
            }
          : (e) => {
              e.stopPropagation();

              addBookmark
                .mutateAsync({ contentId: contentId!, contentType: contentType! })
                .then(() => {
                  showToast('add');
                });
            }
      }
    />
  );
}
